/* eslint-disable no-unused-vars */
/* ============
 * Organization Conf Actions
 * ============
 *
 * The actions available for the organization conf module.
 */

import store from '@/store';
import organizationConfApi from '@/api/OrganizationConfApi';
import signWorkflowApi from '@/api/SignWorkflowApi';
import docTypesApi from '@/api/DocTypesApi';
import * as types from './mutation-types';


/**
 * Action fired when the configuration will be fetched.
 *
 * @param {function} commit Commit function to update the store.
 * @param {Number} organizationId   The id of the organization conf that will be retrieved.
 * @param {function} fn     Callback to edit the parameters on the api.
 */
const conf = ({ commit }, organizationId, fn = null) => {
  if (typeof fn === 'function') {
    fn(organizationConfApi);
  }
  store.dispatch('application/startLoading');
  return organizationConfApi.all(organizationId)
    .then((response) => {
      store.dispatch('application/finishLoading');
      let data = {};
      if (response.data) {
        data = response.data;
      }
      commit(types.CONF, data);
      return data;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: '',
      });
      return Promise.reject(error);
    });
};

const confDeskDaf = ({ commit }, organizationId, fn = null) => {
  if (typeof fn === 'function') {
    fn(organizationConfApi);
  }
  store.dispatch('application/startLoading');
  return organizationConfApi.deskDafConf(organizationId)
    .then((response) => {
      store.dispatch('application/finishLoading');
      let data = {};
      if (response.data) {
        data = response.data;
      }
      commit(types.CONF, data);
      return data;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: '',
      });
      return Promise.reject(error);
    });
};


/**
 * Action fired when docTypes will be fetched.
 *
 * @param {function} commit Commit function to update the store.
 */
const allDocTypes = ({ commit }, session) => {
  if (session === 'DeskRh') {
    docTypesApi.docTypes()
      .then((response) => {
        store.dispatch('application/finishLoading');
        commit(types.DOC_TYPES, response);
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: '',
        });
        return Promise.reject(error);
      });
  } else if (session === 'DeskDaf') {
    docTypesApi.docTypesDeskDaf()
      .then((response) => {
        store.dispatch('application/finishLoading');
        commit(types.DOC_TYPES, response);
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: '',
        });
        return Promise.reject(error);
      });
  }
};

/**
 * Action fired when an parser will be updated.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateParsers = ({ state, commit }, payload) => {
  if (payload.session === 'DeskRh') {
    store.dispatch('application/startLoading');
    commit(types.UPDATE_PARSERS, payload.parser);
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parsers mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la mis à jour de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    store.dispatch('application/startLoading');
    commit(types.UPDATE_PARSERS, payload.parser);
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parsers mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la mis à jour de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired when an parser will be created.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const addParser = ({ state, commit }, payload) => {
  if (payload.session === 'DeskRh') {
    store.dispatch('application/startLoading');
    commit(types.ADD_PARSER, payload.parser);
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parser ajouté avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    store.dispatch('application/startLoading');
    commit(types.ADD_PARSER, payload.parser);
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parser ajouté avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return null;
};


/**
 * Action fired when extra options will be updated.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateExtraOptions = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  commit(types.UPDATE_EXTRA_OPTIONS, payload.extraOptions);
  if (payload.session === 'DeskRh') {
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Services souscrits mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la mis à jour de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Services souscrits mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la mis à jour de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired when an parser will be created.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateDocTypesWithStorage = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  commit(types.ADD_DOCTYPES_WITHSTORAGE, payload.docTypeWithStorage);
  if (payload.session === 'DeskRh') {
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Document dématérialisé ajouté avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Document dématérialisé ajouté avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired when a parser will be destroyed.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const destroyParser = ({ state, commit }, payload) => {
  if (payload.session === 'DeskRh') {
    commit(types.REMOVE_PARSER, payload.parser);
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parser supprimé avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    commit(types.REMOVE_PARSER, payload.parser);
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Parser supprimé avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return store.dispatch('application/startLoading');
};

/**
 * Action fired when a parser will be destroyed.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const destroyDocTypeWithStorage = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  if (payload.session === 'DeskRh') {
    commit(types.REMOVE_DOCTYPES_WITHSTORAGE, payload.docTypeWithStorage);
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Document dématérialisé supprimé avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    commit(types.REMOVE_DOCTYPES_WITHSTORAGE, payload.docTypeWithStorage);
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Document dématérialisé supprimé avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création de l\'organisation',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired in a cascade check.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const checkCascade = ({ state, commit }, payload) => {
  const jsonCascadeCheck = {
    fieldsToUpdate: payload.fieldsToUpdate,
  };
  if (payload.extraOptionsKeys != null && payload.extraOptionsKeys.length) {
    jsonCascadeCheck.extraOptionsKeys = payload.extraOptionsKeys;
  }
  // API CALL
  store.dispatch('application/startLoading');
  if (payload.session === 'DeskRh') {
    return organizationConfApi.checkCascade(payload.organizationId, jsonCascadeCheck)
      .then((response) => {
        store.dispatch('application/finishLoading');
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la récupération des données sur les enfants.',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.checkDeskDafCascade(payload.organizationId, jsonCascadeCheck)
      .then((response) => {
        store.dispatch('application/finishLoading');
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la récupération des données sur les enfants.',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired in a cascade update.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateCascade = ({ state, commit }, payload) => {
  const jsonCascadeUpdate = {
    fieldsToUpdate: payload.fieldsToUpdate,
    orgConf: payload.orgConf,
  };
  if (payload.extraOptionsKeys != null && payload.extraOptionsKeys.length) {
    jsonCascadeUpdate.extraOptionsKeys = payload.extraOptionsKeys;
  }
  // API CALL
  store.dispatch('application/startLoading');
  if (payload.session === 'DeskRh') {
    return organizationConfApi.updateCascade(payload.organizationId, jsonCascadeUpdate)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Réplication en cascade effectuée avec succès.',
        });
        return response;
      }).catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la réplication en cascade.',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.updateDeskDafCascade(payload.organizationId, jsonCascadeUpdate)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Réplication en cascade effectuée avec succès.',
        });
        return response;
      }).catch((error) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la réplication en cascade.',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired when printing infos will be updated.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updatePrintingInfos = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  if (payload.session === 'DeskRh') {
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Informations éditique mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création des informations d\'éditique',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        store.dispatch('application/openSnackbar', {
          message: 'Informations éditique mis à jour avec succès',
        });
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la création des informations d\'éditique',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

/**
 * Action fired when sign workflows are initialized.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const initSignWorkflow = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  return signWorkflowApi.findByOrgId(payload)
    .then((response) => {
      store.dispatch('application/finishLoading');
      return response;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Erreur lors de l\'initialisation des parapheurs modèles.',
      });
      return Promise.reject(error);
    });
};

/**
 * Action fired when sign workflows are updated.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateSignWorkflow = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  return signWorkflowApi.update(payload)
    .then((response) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Parapheurs modèles mis à jour avec succès.',
      });
      return response;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Erreur lors de la mise à jour du parapheur modèle.',
      });
      return Promise.reject(error);
    });
};

/**
 * Action fired when a sign workflow is created.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const createSignWorkflow = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  return signWorkflowApi.create(payload)
    .then((response) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Parapheur créé avec succès.',
      });
      return response;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Erreur lors de la création du parapheur modèle.',
      });
      return Promise.reject(error);
    });
};

/**
 * Action fired when a sign workflow is deleted.
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const deleteSignWorkflow = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  return signWorkflowApi.delete(payload)
    .then((response) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Parapheur supprimé avec succès.',
      });
      return response;
    })
    .catch((error) => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: 'Erreur lors de la suppression du parapheur modèle.',
      });
      return Promise.reject(error);
    });
};

/**
 * Action fired when a checkbox is checked on fileTypes screen
 *
 * @param {Object} state The current state of the store.
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   payload   The payload.
 */
const updateDispatchValidation = ({ state, commit }, payload) => {
  store.dispatch('application/startLoading');
  commit(types.UPDATE_DISPATCH_VALIDATION, payload);
  if (payload.session === 'DeskRh') {
    return organizationConfApi.update(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.conf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la modification de la distribution.',
        });
        return Promise.reject(error);
      });
  } else if (payload.session === 'DeskDaf') {
    return organizationConfApi.updateDeskDaf(payload.organizationId, state.conf)
      .then((response) => {
        store.dispatch('application/finishLoading');
        return response;
      })
      .catch((error) => {
        store.dispatch('application/finishLoading');
        this.confDeskDaf(payload.organizationId);
        store.dispatch('application/openSnackbar', {
          message: 'Erreur lors de la modification de la distribution.',
        });
        return Promise.reject(error);
      });
  }
  return null;
};

export default {
  conf,
  confDeskDaf,
  allDocTypes,
  addParser,
  updateParsers,
  updateDocTypesWithStorage,
  updateExtraOptions,
  destroyParser,
  destroyDocTypeWithStorage,
  checkCascade,
  updateCascade,
  updatePrintingInfos,
  initSignWorkflow,
  updateSignWorkflow,
  createSignWorkflow,
  deleteSignWorkflow,
  updateDispatchValidation,
};



// WEBPACK FOOTER //
// ./src/store/modules/organization-conf/actions.js